import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { WALL_TYPE } from '@cx-sdk/constants';
import { type RequestContext } from '@cx-web/domain-types';
import { type Subscription } from '@cx-web/domain-types/myoc';
import Betslip from '@cx-web/feature-betslip/components/betslip';
import MiniBetslip from '@cx-web/feature-betslip/components/components/mini-betslip';
import { useBetslipContext } from '@cx-web/feature-betslip/context';
import { useLabelActions } from '@cx-web/feature-i18n';
import { useMyOCUser } from '@cx-web/feature-myoc';
import {
  Authentication,
  Dialog as AuthDialog,
  DialogContent,
  DialogTitle,
  InfoLink,
  useAuthModal,
  useAuthModalFromQueryParams,
  VIEW_LOGIN,
  VIEW_VERIFY_PAYMENT,
} from '@cx-web/feature-myoc-login';
import { type RepubIs } from '@cx-web/utils';
import { pollForWindowProperty } from '@cx-web/utils/pollForWindowObject';
import classNames from 'classnames';
import { Menu } from 'lucide-react';
import { ClientOnly } from 'remix-utils/client-only';

import { type Country, type Repub } from '@cx-modules/types';

import Icon from '~/components/Icon';
import { useCurrentPath } from '~/hooks/use-current-path';
import { createPositiveEvConfig } from '~/pages/config';
import { Button } from '~/vendor/cxy/components/Button';
import { Dialog } from '~/vendor/cxy/components/Dialog';

import styles from './styles/Header.module.scss';
import { HeaderItems } from './HeaderItems.tsx';
import { MenuList } from './MenuList.tsx';
import miniBetslipStyles from './mini-betslip.module.css';

/** Overlay that covers the main content when betslip is open on GZ */
const GzBetslipOverlay = () => {
  const { open, toggleBetslip } = useBetslipContext();
  if (!open) return null;
  return <div className={styles.gzBetslipOverlay} onClick={() => toggleBetslip(false)} />;
};

export interface HeaderProps {
  BETSLIP_SCRIPT_URL: string;
  apiKey: string;
  baseUrl: string;
  countryCode: Country;
  currentUrl: string;
  deviceId: string;
  googleID: string;
  isTipstersEnabled: boolean;
  prosperStackUrl: string;
  repub: Repub;
  repubIs: RepubIs;
  repubPath: string;
  requestContext: RequestContext;
  resourcesUrl: string;
  subscriptions?: Subscription[];
}

export const Header = ({
  BETSLIP_SCRIPT_URL,
  apiKey,
  baseUrl,
  countryCode,
  currentUrl,
  deviceId,
  googleID,
  isTipstersEnabled,
  prosperStackUrl,
  repub,
  repubIs,
  repubPath,
  requestContext,
  resourcesUrl,
  subscriptions,
}: HeaderProps) => {
  const currentPath = useCurrentPath();
  const { userOddsFormat } = useBetslipContext();
  const currentPathWithRepub = `${repubPath}${currentPath}`;
  const labelActions = useLabelActions();
  const { t } = useTranslation();
  const { myocData } = useMyOCUser();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const { isOpen, toggle, view } = useAuthModal();
  const navigate = useNavigate();
  // defaultShouldRevalidate: false — the strip is address-bar hygiene (it also clears
  // oc_prefill_email); no refetch needed, SocialButtons strips currentUrl itself.
  useAuthModalFromQueryParams({
    onStrip: strippedHref =>
      navigate(strippedHref, { defaultShouldRevalidate: false, preventScrollReset: true, replace: true }),
    skipOpen: !!myocData?.myOCUser,
    views: [VIEW_LOGIN],
  });
  const config = createPositiveEvConfig(requestContext);
  const isPaywalled = config.isWalled && config.wallType === WALL_TYPE.PAYWALL;
  const isVerifyingPayment = view === VIEW_VERIFY_PAYMENT;

  const [betCount, setBetCount] = useState(0);
  const betslipOriginalsRef = useRef<{
    addBet: any;
    removeBet: any;
  } | null>(null);

  useEffect(() => {
    if (!repubIs.OC) return;

    window.oc = {
      betslipAdvertActive: false,
    };

    let xpId = '';
    if (typeof window.xtremepush !== 'undefined') {
      const xpDeviceInfo = window.xtremepush('get', 'device_info');
      if (typeof xpDeviceInfo !== 'undefined') {
        xpId = xpDeviceInfo.id;
      }
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const loadBetslip = async () => {
      const script = document.createElement('script');
      script.src = BETSLIP_SCRIPT_URL;
      script.type = 'text/javascript';
      script.async = true;
      document.head.appendChild(script);

      try {
        await pollForWindowProperty('ocRenderers.integrated-betslip');
      } catch {
        console.warn('OC Betslip renderer not present');
        return;
      }

      const initBetslip = window.ocRenderers?.['integrated-betslip'];

      initBetslip({
        apiKey,
        deviceId,
        oddsFormat: userOddsFormat,
        repub,
        xpId,
      });

      try {
        await pollForWindowProperty('ocBetslip');
      } catch {
        console.warn('OC Betslip not loaded');
        return;
      }

      const betslip = window.ocBetslip;
      if (!betslip) return;

      if (!betslipOriginalsRef.current) {
        betslipOriginalsRef.current = {
          addBet: betslip.addBet.bind(betslip),
          removeBet: betslip.removeBet.bind(betslip),
        };
      }

      const { addBet: originalAddBet, removeBet: originalRemoveBet } = betslipOriginalsRef.current;

      betslip.addBet = (bet: any, showBookieSelector?: boolean) => {
        const result = originalAddBet(bet, showBookieSelector);
        window.dispatchEvent(
          new CustomEvent('oc-betslip:bet-added', {
            detail: { betId: bet.betId },
          }),
        );
        setBetCount(betslip.getBetCount());
        return result;
      };

      betslip.removeBet = ({ betId }: { betId: number }) => {
        const result = originalRemoveBet({ betId });
        window.dispatchEvent(
          new CustomEvent('oc-betslip:bet-removed', {
            detail: { betId },
          }),
        );
        setBetCount(betslip.getBetCount());
        return result;
      };

      betslip.setCallbacks({
        removeAllBets: () => {
          window.dispatchEvent(new CustomEvent('oc-betslip:cleared'));
          setBetCount(0);
        },
      });

      setBetCount(betslip.getBetCount());

      let lastBetCount = betslip.getBetCount();
      intervalId = setInterval(() => {
        const currentCount = betslip.getBetCount();

        if (currentCount === lastBetCount) return;

        if (lastBetCount > 0 && currentCount === 0) {
          window.dispatchEvent(new CustomEvent('oc-betslip:cleared'));
        }

        setBetCount(currentCount);
        lastBetCount = currentCount;
      }, 1000);
    };

    loadBetslip();

    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
      if (betslipOriginalsRef.current && window.ocBetslip) {
        window.ocBetslip.addBet = betslipOriginalsRef.current.addBet;
        window.ocBetslip.removeBet = betslipOriginalsRef.current.removeBet;
      }
    };
  }, [BETSLIP_SCRIPT_URL, apiKey, userOddsFormat, repub, repubIs, deviceId]);

  return (
    <>
      <header className={styles.header} data-testid="oc-plus-header">
        <a className={styles.isVisiblyHidden} href={`${currentPathWithRepub}#main`}>
          Skip to main content
        </a>

        <Dialog
          className={styles.scroll}
          isFullScreen
          isOpen={isMenuOpen}
          setIsOpen={setIsMenuOpen}
          title="navigation"
          triggerRef={ref}
        >
          <div style={{ width: '100%' }}>
            <h3 className={styles.mobileMenuTitle}>{!repubIs.GZ ? t('CX_PLUS_MENU') : t('TOOLS')}</h3>
            <HeaderItems
              baseUrl={baseUrl}
              currentPathWithRepub={currentPathWithRepub}
              isTipstersEnabled={isTipstersEnabled}
              repub={repub}
              resourcesUrl={resourcesUrl}
            />
          </div>
        </Dialog>

        <nav aria-label="Main" className={styles.navigation}>
          <Dialog.Trigger
            aria-label="Toggle navigation Menu"
            // @ts-ignore
            className={styles.menuButton}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            ref={ref}
          >
            {!repubIs.GZ ? <Menu /> : <span className={styles.menuButtonGz}>{t('TOOLS')}</span>}
          </Dialog.Trigger>

          <HeaderItems
            baseUrl={baseUrl}
            currentPathWithRepub={currentPathWithRepub}
            isTipstersEnabled={isTipstersEnabled}
            repub={repub}
            resourcesUrl={resourcesUrl}
          />

          <div className={styles.userButtons}>
            {!repubIs.GZ && (
              <MenuList
                isLoggedIn={!!myocData?.myOCUser}
                prosperStackUrl={prosperStackUrl}
                repub={repub}
                subscriptions={subscriptions}
              />
            )}
            {!repubIs.WS && (
              <Button
                aria-label="toggle betslip"
                className={styles.betslipButton}
                variant="outline"
                {...(repubIs.OC && {
                  onClick: () => {
                    window.ocBetslip?.toggle();
                  },
                })}
              >
                {repubIs.OC ? (
                  <div className={miniBetslipStyles.wrapper} data-testid="betslip-button">
                    <div className={classNames(miniBetslipStyles.miniBetslip, styles.betslipMini)}>
                      <Icon height={20} icon="Betslip" width={20} />
                      {betCount > 0 && (
                        <span
                          className={[miniBetslipStyles.betCount, betCount > 0 ? miniBetslipStyles.betAdded : ''].join(
                            ' ',
                          )}
                        >
                          {betCount}
                        </span>
                      )}
                    </div>
                    <span className={miniBetslipStyles.betslipText}>Betslip</span>
                  </div>
                ) : (
                  <ClientOnly fallback={null}>
                    {() => (
                      <MiniBetslip
                        betslipTitle={labelActions.get('RESPONSIVE_BETSLIP')}
                        className={styles.betslipMini}
                        variant="pill"
                      />
                    )}
                  </ClientOnly>
                )}
              </Button>
            )}
          </div>
        </nav>

        <ClientOnly fallback={null}>
          {() => <>{(repubIs.US || repubIs.GZ) && <Betslip className={styles.betslip} country={countryCode} />}</>}
        </ClientOnly>
        {repubIs.GZ && <GzBetslipOverlay />}
      </header>

      <AuthDialog onOpenChange={toggle} open={isOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogTitle style={{ display: 'none' }}>Log in or Sign up</DialogTitle>
          <Authentication
            currentUrl={currentUrl}
            googleID={googleID}
            infoLink={isPaywalled && !isVerifyingPayment ? <InfoLink /> : undefined}
            resourcesUrl={resourcesUrl}
            view={view}
          />
        </DialogContent>
      </AuthDialog>
    </>
  );
};
